import type { Job, LocalFile } from "@stryker/core/types/jobs";
import FileUploader from "@/components/FileUploader";
import AttachedDocument from "@/components/job/AttachedDocument";
import { Typography } from "@/components/ui";

type MergedDoc = Array<LocalFile | Job["attached_documents"][number]>;

interface DocumentUploaderProps {
  documents: MergedDoc;
  referenceDocuments: MergedDoc;
  onDocumentAdd: (file: LocalFile) => void;
  onDocumentRemove: (name: string) => void;
  onReferenceDocumentAdd: (file: LocalFile) => void;
  onReferenceDocumentRemove: (name: string) => void;
}

export function DocumentUploader({
  documents,
  referenceDocuments,
  onDocumentAdd,
  onDocumentRemove,
  onReferenceDocumentAdd,
  onReferenceDocumentRemove,
}: DocumentUploaderProps) {
  function handleDocumentClick(document: LocalFile | Job["attached_documents"][number]) {
    if ("link" in document) {
      window.open(document.link, "blank");
    }
  }

  return (
    <div className="px-6">
      <Typography variant="text-lg" weight="semibold" className="mb-6 border-b border-solid border-gray-200 pb-5">
        Attach to report/ reference documents
      </Typography>
      <div className="flex flex-col gap-4 md:flex-row">
        <div className="flex-1 md:w-1/2">
          <Typography className="text-center" variant="display-xs" weight="semibold">
            Attach to report
          </Typography>
          <div className="flex flex-wrap gap-4">
            {documents.map((d) => (
              <AttachedDocument
                key={d.name}
                document={d}
                onClick={() => handleDocumentClick(d)}
                onDelete={() => onDocumentRemove(d.name)}
              />
            ))}
          </div>
          <div className="flex flex-1 flex-col gap-2">
            <FileUploader
              withPreview={false}
              setFile={onDocumentAdd}
              description="PNG,JPG,PDF, DOCX"
              fileTypesRegex={/\.(jpe?g|png|pdf|docx)$/i}
              inputAcceptFiles="image/png, image/jpeg, image/gif, image/svg+xml, application/pdf, application/msword, text/plain, .docx"
            />
          </div>
        </div>
        <div className="flex-1 border-solid border-black pl-4 md:w-1/2 md:border-l">
          <Typography className="text-center" variant="display-xs" weight="semibold">
            Reference Documents
          </Typography>
          <div className="flex flex-wrap gap-4">
            {referenceDocuments.map((d) => (
              <AttachedDocument
                key={d.name}
                document={d}
                onDelete={() => onReferenceDocumentRemove(d.name)}
                onClick={() => handleDocumentClick(d)}
              />
            ))}
          </div>
          <div className="flex flex-1 flex-col gap-2">
            <FileUploader
              className="flex-1"
              withPreview={false}
              setFile={onReferenceDocumentAdd}
              description="PNG,JPG,PDF, DOCX"
              fileTypesRegex={/\.(jpe?g|png|pdf|docx)$/i}
              inputAcceptFiles="image/png, image/jpeg, image/gif, image/svg+xml, application/pdf, application/msword, text/plain, .docx"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
